export const FREQUENCY_OPTIONS = [
  { value: "od", label: "OD" },
  { value: "mane", label: "Mane" },
  { value: "bd", label: "BD" },
  { value: "tds", label: "TDS" },
  { value: "qds", label: "QDS" },
  { value: "nocte", label: "Nocte" },
  { value: "sos", label: "SOS" },
  { value: "prn", label: "PRN" },
  { value: "stat", label: "Stat" },
  { value: "stat , 100mg/nocte", label: "Stat, 100mg/nocte" },
];

const FREQ_PER_DAY: Record<string, number | null> = {
  od: 1,
  mane: 1,
  nocte: 1,
  bd: 2,
  tds: 3,
  qds: 4,
  stat: 1,
  sos: null,
  prn: null,
};

const FORMULA_SCALES = new Set(["tablet", "capsule", "u", "tab", "cap"]);
const PLAIN_SCALES = new Set(["bottle", "tube", "ml", "g", "m"]);

export function doseDisplayValue(dose: any): string {
  if (dose == null) return "";
  if (typeof dose === "object") return String(dose.value ?? "").trim();
  return String(dose).trim();
}

export function doseOptionId(dose: any): string {
  if (dose != null && typeof dose === "object" && dose._id) {
    return String(dose._id);
  }
  return doseDisplayValue(dose);
}

export function usesFormulaQty(scale?: string | null): boolean {
  const s = String(scale || "")
    .trim()
    .toLowerCase();
  if (PLAIN_SCALES.has(s)) return false;
  if (FORMULA_SCALES.has(s)) return true;
  return true;
}

export function calculatePrescriptionQty(
  frequency: string,
  durationDays: string | number,
  scale?: string | null
): number | null {
  if (!usesFormulaQty(scale)) return null;
  const freqKey = String(frequency || "")
    .trim()
    .toLowerCase()
    .split(",")[0]
    .trim();
  if (freqKey === "sos" || freqKey === "prn") return null;
  const perDay = FREQ_PER_DAY[freqKey];
  if (perDay == null) return null;
  if (freqKey === "stat") return 1;
  const days = parseInt(String(durationDays ?? ""), 10);
  if (!Number.isFinite(days) || days <= 0) return null;
  return perDay * days;
}

/**
 * Display dose for a saved prescription line. `doseId` holds a dose subdoc
 * _id (post-migration) or a legacy/orphan value string; resolve it against
 * the populated drug's dose list, else fall back to whatever label we have.
 */
export function resolveDoseValue(line: any): string {
  const doseKey = String(line?.doseId ?? "").trim();
  if (doseKey) {
    const doses = normalizeDoseList(line?.drugsId?.dose);
    const match = doses.find(
      (d) => d._id === doseKey || d.value === doseKey
    );
    if (match) return match.value;
  }
  const label = doseDisplayValue(line?.dose);
  if (label && label !== doseKey) return label;
  return doseKey;
}

/**
 * Derived line status — never separately maintained:
 * settled → Provided (sold), doctor flag → To provide, else Prescribed only.
 */
export function provideStatusLabel(line: {
  dispense?: boolean;
  settled?: boolean;
}): string {
  if (line?.settled) return "Provided";
  if (line?.dispense) return "To provide";
  return "Prescribed only";
}

/** Normalize catalog dose array for UI (always { _id?, value }). */
export function normalizeDoseList(doses: any): { _id?: string; value: string }[] {
  if (!Array.isArray(doses)) return [];
  return doses
    .map((d) => {
      if (d != null && typeof d === "object") {
        return {
          _id: d._id ? String(d._id) : undefined,
          value: String(d.value ?? "").trim(),
        };
      }
      return { value: String(d ?? "").trim() };
    })
    .filter((d) => d.value);
}
